import { Alert, Button, Group, Loader, Modal, Stack, Text } from '@mantine/core'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { IconExclamationMark } from '@tabler/icons-react'
import { useState } from 'react'

interface PaymentMethodUpdateModalProps {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
  clientSecret?: string | null
  isExistingMethod?: boolean
  isPolling?: boolean
}

export function PaymentMethodUpdateModal({
  opened,
  onClose,
  onSuccess,
  clientSecret,
  isExistingMethod = false,
  isPolling = false,
}: PaymentMethodUpdateModalProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements || !clientSecret) {
      return
    }

    setIsProcessing(true)
    setErrorMessage(null)

    try {
      // For Setup Intents (updating/adding payment methods without charging)
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/dashboard/billing`,
        },
        redirect: 'if_required',
      })

      if (error) {
        setErrorMessage(error.message || 'An unexpected error occurred.')
      } else {
        // Setup succeeded - payment method saved
        onSuccess()
      }
    } catch (_err) {
      setErrorMessage('An unexpected error occurred.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCancel = () => {
    if (!isProcessing && !isPolling) {
      onClose()
    }
  }

  const isFormDisabled = !stripe || !elements || !clientSecret || isProcessing || isPolling

  const getTitle = () => {
    if (isExistingMethod) {
      return 'Update Payment Method'
    }
    return 'Add Payment Method'
  }

  const getDescription = () => {
    if (isExistingMethod) {
      return 'Update your payment information below. This will not charge your card.'
    }
    return 'Add a new payment method to your account. This will not charge your card.'
  }

  const getButtonText = () => {
    if (isProcessing) {
      return isExistingMethod ? 'Updating...' : 'Adding...'
    }
    if (isPolling) {
      return 'Processing...'
    }
    return isExistingMethod ? 'Update Payment Method' : 'Add Payment Method'
  }

  // Show loading state while client secret is being generated
  if (!clientSecret) {
    return (
      <Modal opened={opened} onClose={handleCancel} title={getTitle()} size="md" centered>
        <Stack gap="xl" align="center" py="xl">
          <Stack gap="xs" align="center">
            <Text c="dimmed" ta="center" size="lg">
              Setting up payment method...
            </Text>
          </Stack>
          <Loader size="lg" />
        </Stack>
      </Modal>
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={getTitle()}
      size="lg"
      centered
      closeOnClickOutside={!isProcessing}
      closeOnEscape={!isProcessing}>
      <form onSubmit={handleSubmit}>
        <Stack gap="lg">
          <Text c="dimmed" size="sm">
            {getDescription()}
          </Text>

          {/* Stripe Payment Element configured for Setup Intent */}
          <div style={{ minHeight: '200px' }}>
            <PaymentElement
              options={{
                layout: 'tabs',
                paymentMethodOrder: ['card', 'us_bank_account'],
                // For setup intents, we want to focus on saving the payment method
                fields: {
                  billingDetails: 'auto',
                },
              }}
            />
          </div>

          {/* Error Display */}
          {errorMessage && (
            <Alert variant="light" color="red" title="Setup Failed" icon={<IconExclamationMark />}>
              {errorMessage}
            </Alert>
          )}

          {/* Action Buttons */}
          <Group justify="space-between" mt="md">
            <Button variant="default" onClick={handleCancel} disabled={isFormDisabled} size="md">
              Cancel
            </Button>
            <Button type="submit" disabled={isFormDisabled} size="md" color="indigo">
              {isProcessing || isPolling ? (
                <Group gap="xs">
                  <Loader size="xs" />
                  <span>{getButtonText()}</span>
                </Group>
              ) : (
                getButtonText()
              )}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
